import { Eye, EyeOff } from "lucide-react";
import { type ChangeEvent, type FocusEvent, type KeyboardEvent, useState } from "react";

import { getSanitizedHtml } from "../../../handlers/utilityFunctions";
import { cn } from "../../../lib/utils";
import Icon from "../../Icon";
import Tips from "../../Tips";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

interface InputFieldProps {
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	value?: string;
	id?: string;
	name?: string;
	type?: string;
	placeholder?: string;
	error?: string | null;
	warn?: string | null;
	disabled?: boolean;
	label?: string;
	onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
	onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
	initialPasswordVisibilityStatus?: boolean;
	onClick?: (visible: boolean) => void;
	iconLeft?: string;
	disableCopyPaste?: boolean;
	tips?: { label?: string; content: string[] } | null;
	className?: string;
	required?: boolean;
}

function InputField({
	onChange,
	value,
	id,
	name,
	type,
	placeholder,
	error,
	warn,
	disabled,
	label,
	onFocus,
	onKeyDown,
	onBlur,
	initialPasswordVisibilityStatus,
	onClick,
	iconLeft,
	disableCopyPaste,
	tips = null,
}: InputFieldProps) {
	const [passwordVisible, setPasswordVisible] = useState(initialPasswordVisibilityStatus);

	const togglePasswordVisibility = () => {
		const newVisibility = !passwordVisible;
		setPasswordVisible(newVisibility);
		onClick?.(newVisibility);
	};

	const handleCopyPaste = (event: React.ClipboardEvent) => {
		if (disableCopyPaste) {
			event.preventDefault();
		}
	};

	return (
		<div className="relative space-y-1">
			<div className="flex items-center justify-between">
				{label && <Label className="text-sm">{label}</Label>}
				{tips && <Tips content={tips} />}
			</div>

			<div className="relative">
				{iconLeft && (
					<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
						<Icon iconName={iconLeft} size={16} />
					</div>
				)}
				<Input
					type={type === "password" && !passwordVisible ? "password" : type === "password" ? "text" : type}
					name={name}
					id={id}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					onFocus={onFocus}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					disabled={disabled}
					autoComplete="off"
					onCopy={handleCopyPaste}
					onPaste={handleCopyPaste}
					className={cn(iconLeft && "pl-11", type === "password" && "pr-10")}
				/>

				{type === "password" && (
					<button
						type="button"
						className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
						onClick={togglePasswordVisibility}
					>
						{passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
					</button>
				)}
			</div>

			{error && <div className="text-sm font-medium leading-tight text-red-500">{getSanitizedHtml(error)}</div>}
			{warn && <div className="text-sm font-medium leading-tight text-yellow-500">{getSanitizedHtml(warn)}</div>}
		</div>
	);
}

export default InputField;
